/**
 * Page scrolling helpers for code that has to move or read the page scroll
 * position: the chip jumps in new-menu / new-dine-in, the header's
 * hide-on-scroll, the route-change reset in AppComponent, the legacy pages.
 *
 * The viewport is the page's scroll container (see styles.scss). <body> used
 * to be, which is why these read and write <body> as well as <html>: with
 * <body> scrolling, `window.scrollTo` did nothing and
 * `element.scrollIntoView({ behavior: 'smooth' })` was unreliable on Android
 * Chrome (the dine-in chips didn't move the page at all), so jumps are
 * computed offsets. Both are still touched so these keep working if either
 * element scrolls again (or in quirks mode).
 */

/** Current page scroll offset, whichever element is the scroller. */
export function pageScrollY(): number {
  return Math.max(
    document.body.scrollTop,
    document.documentElement.scrollTop,
    window.scrollY || 0
  );
}

export function scrollPageTo(top: number, behavior: ScrollBehavior) {
  document.body.scrollTo({ top, behavior });
  document.documentElement.scrollTo({ top, behavior });
}

/**
 * Hold the new header's hide-on-scroll for a programmatic jump.
 *
 * The header slides away on a downward scroll and back on an upward one. A
 * chip jump scrolls the page (often up, then a small correction down), which
 * made the bar bounce in and out on every tap. While a hold is active the
 * header ignores scroll direction and keeps the state asked for here —
 * `hidden: true` slides it away (where it is allowed to hide at all), so the
 * section lands right under the sticky category strip.
 */
export const headerScrollHold = { until: 0, hidden: false };

export function holdHeader(hidden: boolean, ms = 1000) {
  headerScrollHold.hidden = hidden;
  headerScrollHold.until = Date.now() + ms;
}

export function headerHeld(): boolean {
  return Date.now() < headerScrollHold.until;
}
